import React, { useState } from "react";
import {
    Box,
    LinearProgress,
    Link,
    Typography,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Dialog,
    DialogContent,
    Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import CloseIcon from "@mui/icons-material/Close";
import TermsOfService from "./pages/TermsOfService";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ExploreIcon from "@mui/icons-material/Explore";
import { Configuration, Login } from "./reckon-api";
import { LoginApi } from "./reckon-api/apis/LoginApi";

// Styled TextField for the login form
const StyledTextField = ({ label, ...props }: any) => (
    <TextField
        {...props}
        label={label}
        variant="outlined"
        fullWidth
        sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(30, 80, 120, 0.6)',
                borderRadius: '8px',
                color: '#fff',
                '& fieldset': {
                    borderColor: 'rgba(100, 160, 200, 0.4)',
                    borderWidth: '1px',
                },
                '&:hover fieldset': {
                    borderColor: 'rgba(100, 180, 220, 0.7)',
                },
                '&.Mui-focused fieldset': {
                    borderColor: '#4da8da',
                    borderWidth: '2px',
                },
            },
            '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                    color: '#4da8da',
                },
            },
            '& .MuiInputBase-input': {
                color: '#fff',
            },
        }}
    />
);

const SlideUp = React.forwardRef(function SlideUp(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function Copyright(props: { onOpenTerms: () => void }) {
    return (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
                Copyright © <strong style={{ color: '#fff' }}>Reckon Bolt</strong> {new Date().getFullYear()}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Link
                    href="/terms-of-service"
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        props.onOpenTerms();
                    }}
                    sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem', textDecoration: 'none', '&:hover': { color: '#4da8da' }, cursor: 'pointer' }}
                >
                    Terms of Service
                </Link>
                <Link href="#" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem', textDecoration: 'none', '&:hover': { color: '#4da8da' } }}>
                    Privacy Policy
                </Link>
                <Link href="#" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem', textDecoration: 'none', '&:hover': { color: '#4da8da' } }}>
                    Legal Information
                </Link>
            </Box>
        </Box>
    );
}

export default function LoginPage(props: { setToken: (token: Login) => void }) {
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const api_config = new Configuration({
            basePath: 'https://api.reckon-bolt.com',
        });
        const data = new FormData(e.currentTarget);
        const username = data.get("username") as string;
        const password = data.get("password") as string;
        const api = new LoginApi(api_config);
        return api.get({ username, password, token: "gimme" }).then((user: Login) => {
            props.setToken(user);
            localStorage.setItem('reckonBoltUser', JSON.stringify(user));
        }).catch(() => setLoading(false));
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
            {/* Left Panel - Branding */}
            <Box
                sx={{
                    flex: 1,
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #0a1628 0%, #1a2d4a 50%, #0d1b2a 100%)',
                }}
            >
                {/* Background Image */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: "url(https://images.contentstack.io/v3/assets/bltb6530b271fddd0b1/blt180ec68d4e498c7f/6310c9eb47e2c05811ecca9a/Val_Ep5_Background.jpg)",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                        backgroundPosition: "center top",
                        opacity: 0.85,
                    }}
                />
                {/* Gradient Overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(180deg, rgba(10, 22, 40, 0.3) 0%, rgba(10, 22, 40, 0.6) 60%, rgba(10, 22, 40, 0.95) 100%)',
                    }}
                />
                {/* Branding Content */}
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        textAlign: 'center',
                        px: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontStyle: 'italic',
                            fontWeight: 700,
                            color: '#fff',
                            textShadow: '2px 4px 20px rgba(0, 0, 0, 0.5)',
                            mb: 1,
                            fontSize: { md: '2.5rem', lg: '3.5rem' },
                        }}
                    >
                        Reckon Bolt
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 300,
                            color: 'rgba(255, 255, 255, 0.85)',
                            letterSpacing: '0.5px',
                        }}
                    >
                        Advanced Valorant Analytics
                    </Typography>
                </Box>
            </Box>

            {/* Right Panel - Login Form */}
            <Box
                sx={{
                    flex: { xs: 1, md: 0.6 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'linear-gradient(180deg, #0a0f18 0%, #05080d 100%)',
                    px: { xs: 3, sm: 6, md: 8 },
                    py: 4,
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 400 }}>
                    {/* Lock Icon with Glow */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 3,
                        }}
                    >
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(255, 255, 255, 0.95) 0%, rgba(180, 220, 255, 0.8) 50%, rgba(100, 160, 220, 0.4) 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 0 40px rgba(100, 180, 255, 0.5), 0 0 80px rgba(100, 180, 255, 0.3)',
                            }}
                        >
                            <LockOutlinedIcon sx={{ fontSize: 36, color: '#1a3a5c' }} />
                        </Box>
                    </Box>

                    {/* Welcome Text */}
                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 700,
                            color: '#fff',
                            textAlign: 'center',
                            mb: 1,
                        }}
                    >
                        Welcome Back
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            textAlign: 'center',
                            mb: 3,
                        }}
                    >
                        Sign in to access your analytics dashboard
                    </Typography>

                    {/* Discover Features Link */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 4,
                        }}
                    >
                        <Link
                            href="#"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                color: '#4da8da',
                                textDecoration: 'none',
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                '&:hover': {
                                    color: '#6fc5f0',
                                },
                            }}
                        >
                            <ExploreIcon sx={{ fontSize: 18 }} />
                            Discover Reckon Bolt Features
                        </Link>
                    </Box>

                    {/* Login Form */}
                    <Box
                        component="form"
                        noValidate
                        onSubmit={(e) => {
                            setLoading(true);
                            handleSubmit(e);
                        }}
                    >
                        <StyledTextField
                            required
                            id="username"
                            label="Username"
                            name="username"
                            autoComplete="username"
                            autoFocus
                        />
                        <StyledTextField
                            required
                            name="password"
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            id="password"
                            autoComplete="current-password"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                                        >
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {loading ? (
                            <LinearProgress
                                sx={{
                                    mt: 2,
                                    mb: 2,
                                    borderRadius: '25px',
                                    height: 8,
                                    backgroundColor: 'rgba(30, 80, 120, 0.4)',
                                    '& .MuiLinearProgress-bar': {
                                        background: 'linear-gradient(90deg, #3a8dbd, #6fc5f0)',
                                    },
                                }}
                            />
                        ) : (
                            <Button
                                type="submit"
                                fullWidth
                                sx={{
                                    mt: 2,
                                    py: 1.5,
                                    borderRadius: '25px',
                                    background: 'linear-gradient(90deg, rgba(60, 120, 160, 0.7) 0%, rgba(100, 160, 200, 0.5) 100%)',
                                    color: '#a8d4eb',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    border: '1px solid rgba(100, 160, 200, 0.3)',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, rgba(70, 140, 180, 0.8) 0%, rgba(120, 180, 220, 0.6) 100%)',
                                        color: '#fff',
                                        boxShadow: '0 6px 30px rgba(100, 180, 255, 0.3)',
                                    },
                                }}
                            >
                                Sign In
                            </Button>
                        )}
                    </Box>

                    <Copyright onOpenTerms={() => setTermsOpen(true)} />
                </Box>
            </Box>

            {/* Terms of Service Dialog */}
            <Dialog
                open={termsOpen}
                onClose={() => setTermsOpen(false)}
                fullScreen
                TransitionComponent={SlideUp}
                PaperProps={{
                    sx: {
                        background: 'linear-gradient(180deg, #0a0f18 0%, #05080d 100%)',
                    },
                }}
            >
                <Box sx={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                    <IconButton
                        onClick={() => setTermsOpen(false)}
                        sx={{
                            color: 'rgba(255,255,255,0.7)',
                            bgcolor: 'rgba(255,255,255,0.08)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    <TermsOfService />
                </DialogContent>
            </Dialog>
        </Box>
    );
}
